import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { Button, Input, Select, Space } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { DateRangePicker as ZhDateRangePicker, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { AdminDetailTable } from "@/views/components/DetailTable";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";

export interface CustomerLogisticsRecord {
    transportOrderShip?: {
        id?: number | string;
        type?: number | string;
        ownerEnterprise?: { name?: string; institutionName?: string; realName?: string };
        sourcePortName?: string;
        destinationPortName?: string;
        shipName?: string;
        upstreamHandlerName?: string;
        downstreamHandlerName?: string;
        unitPrice?: number | string;
        ton?: number | string;
        actualFreightCost?: number | string;
        createdTime?: string;
        state?: number | string;
    };
}

export interface CustomerLogisticsQuery {
    customerId: number;
    page: number;
    size: number;
    keyword?: string;
    type?: number;
    portKeyword?: string;
    shipKeyword?: string;
    handlerName?: string;
    state?: number | number[];
    beginTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface CustomerLogisticsPageProps {
    customerId: number;
    loadLogistics?: (query: CustomerLogisticsQuery) => Promise<{ list?: CustomerLogisticsRecord[]; total?: number }>;
}

const typeLabels: Record<number, string> = { 1: "平台创建", 2: "船主报价", 3: "船代报价" };
const stateLabels: Record<number, string> = {
    3: "待发航",
    4: "运输中",
    5: "待卸货",
    8: "已完成",
    10: "待付定金",
    13: "未完成"
};
const CUSTOMER_LOGISTICS_KEYWORD_BATCH_SIZE = 100;

/**
 * staging 运单接口当前会拒绝旧 Vue 的 keyword 参数；用户详情页按 customerId 拉取全部匹配记录后，
 * 在前端完成编号/货主关键词过滤和分页，既保留筛选能力，也避免接口返回参数错误。
 */
async function loadKeywordCompatibleLogistics(
    loadLogistics: NonNullable<CustomerLogisticsPageProps["loadLogistics"]>,
    query: CustomerLogisticsQuery
) {
    const normalizedKeyword = query.keyword?.trim().toLocaleLowerCase();
    if (!normalizedKeyword) return loadLogistics(query);

    const { keyword: _keyword, page, size, ...stableQuery } = query;
    const firstPage = await loadLogistics({
        ...stableQuery,
        page: 1,
        size: CUSTOMER_LOGISTICS_KEYWORD_BATCH_SIZE
    });
    const total = firstPage.total || 0;
    const allRows = [...(firstPage.list || [])];
    const pageCount = Math.ceil(total / CUSTOMER_LOGISTICS_KEYWORD_BATCH_SIZE);
    if (pageCount > 1) {
        const remainingPages = await Promise.all(
            Array.from({ length: pageCount - 1 }, (_, index) =>
                loadLogistics({
                    ...stableQuery,
                    page: index + 2,
                    size: CUSTOMER_LOGISTICS_KEYWORD_BATCH_SIZE
                })
            )
        );
        remainingPages.forEach(result => allRows.push(...(result.list || [])));
    }

    const filteredRows = allRows.filter(row => {
        const ship = row.transportOrderShip;
        const owner = ship?.ownerEnterprise;
        return [ship?.id, owner?.name, owner?.institutionName, owner?.realName].some(value =>
            String(value ?? "")
                .toLocaleLowerCase()
                .includes(normalizedKeyword)
        );
    });
    const start = (page - 1) * size;
    return {
        list: filteredRows.slice(start, start + size),
        total: filteredRows.length
    };
}
/** 复用 Vue `/admin/transport/order/ship/paging` 的用户物流列表接口。 */
export async function loadCustomerLogistics(query: CustomerLogisticsQuery) {
    const response = await request.service.get("/admin/transport/order/ship/paging", { params: query });
    return response.data as { list?: CustomerLogisticsRecord[]; total?: number };
}

/** 将 zhihao-ui-react 的创建时间排序方向转换为 Vue 运单接口参数。 */
export function getCustomerLogisticsSortParams({
    prop,
    order
}: {
    prop?: string;
    order: "ascending" | "descending" | null;
}): Pick<CustomerLogisticsQuery, "sortKey" | "sortOrder"> {
    if (prop !== "createdTime" || !order) return {};
    return {
        sortKey: "created_time",
        sortOrder: order === "ascending" ? "asc" : "desc"
    };
}

/** 旧 Vue dateTimeFormat 仅展示到分钟，避免 React 表格额外显示秒数。 */
const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 保留运费计算结果供无障碍摘要和既有数据契约使用；旧 Vue 默认列配置不展示该列。 */
const formatFreight = (row: CustomerLogisticsRecord) => {
    const ship = row.transportOrderShip;
    const actualFreightCost = Number(ship?.actualFreightCost || 0);
    const freight = actualFreightCost || Number(ship?.unitPrice || 0) * Number(ship?.ton || 0);
    return freight.toFixed(2);
};

export function CustomerLogisticsPage({
    customerId,
    loadLogistics = loadCustomerLogistics
}: CustomerLogisticsPageProps) {
    const [rows, setRows] = useState<CustomerLogisticsRecord[]>([]);
    const [total, setTotal] = useState(0);
    // 对齐旧 Vue ProTable 的分页状态，确保翻页参数进入 waybill paging 请求。
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [keyword, setKeyword] = useState("");
    const [type, setType] = useState<number>();
    const [portKeyword, setPortKeyword] = useState("");
    const [shipKeyword, setShipKeyword] = useState("");
    const [handlerName, setHandlerName] = useState("");
    // Vue 旧版状态筛选是多选；单项提交时仍压缩为数字以保持接口兼容。
    const [state, setState] = useState<number[]>([]);
    const [beginTime, setBeginTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [submittedQuery, setSubmittedQuery] = useState<Partial<CustomerLogisticsQuery>>({});
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        const query: CustomerLogisticsQuery = {
            customerId,
            page,
            size: pageSize,
            ...submittedQuery
        };
        void loadKeywordCompatibleLogistics(loadLogistics, query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) {
                    setRows([]);
                    setTotal(0);
                    setErrorMessage("物流信息加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [customerId, loadLogistics, page, pageSize, submittedQuery]);

    const submitSearch = () => {
        // 只提交有值的筛选条件，避免空字符串污染 Vue 分页接口参数。
        const nextQuery: Partial<CustomerLogisticsQuery> = {};
        if (keyword.trim()) nextQuery.keyword = keyword.trim();
        if (type != null) nextQuery.type = type;
        if (portKeyword.trim()) nextQuery.portKeyword = portKeyword.trim();
        if (shipKeyword.trim()) nextQuery.shipKeyword = shipKeyword.trim();
        if (handlerName.trim()) nextQuery.handlerName = handlerName.trim();
        if (state.length) nextQuery.state = state.length === 1 ? state[0] : state;
        // 运单分页接口按 ISO 本地时间解析，沿用 Vue dateFormat 的 T 分隔符。
        if (beginTime) nextQuery.beginTime = dayjs(beginTime).format(LOG_DATE_FORMAT_START);
        if (endTime) nextQuery.endTime = dayjs(endTime).format(LOG_DATE_FORMAT_END);
        setSubmittedQuery(nextQuery);
    };

    /** 清空本页全部受控筛选，并立即按第一页默认条件重新加载。 */
    const resetSearch = () => {
        setKeyword("");
        setType(undefined);
        setPortKeyword("");
        setShipKeyword("");
        setHandlerName("");
        setState([]);
        setBeginTime("");
        setEndTime("");
        setPage(1);
        setSubmittedQuery({});
    };

    /** 将旧 Vue 创建时间范围映射为 waybill paging 的整日边界。 */
    const updateCreatedTimeRange = (_value: unknown, formattedValue: [string, string] | null) => {
        setBeginTime(formattedValue?.[0] || "");
        setEndTime(formattedValue?.[1] || "");
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 船运单列表的嵌套字段和展示口径。
    const columns: TableColumnProps<CustomerLogisticsRecord>[] = [
        {
            key: "id",
            label: "编号",
            width: 176,
            readonly: true,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.id ?? DEFAULT_VALUE
        },
        {
            key: "type",
            label: "类型",
            width: 108,
            prop: "transportOrderShip",
            render: ({ row }) => typeLabels[Number(row.transportOrderShip?.type)] || DEFAULT_VALUE
        },
        {
            key: "owner",
            label: "货主",
            width: 120,
            prop: "transportOrderShip",
            render: ({ row }) =>
                row.transportOrderShip?.ownerEnterprise?.name ||
                row.transportOrderShip?.ownerEnterprise?.realName ||
                DEFAULT_VALUE
        },
        {
            key: "sourcePortName",
            label: "始发地",
            width: 148,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.sourcePortName || DEFAULT_VALUE
        },
        {
            key: "destinationPortName",
            label: "目的地",
            width: 148,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.destinationPortName || DEFAULT_VALUE
        },
        {
            key: "shipName",
            label: "船舶",
            width: 204,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.shipName?.split("/")[0] || DEFAULT_VALUE
        },
        {
            key: "upstreamHandlerName",
            label: "上游专员",
            width: 120,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.upstreamHandlerName || DEFAULT_VALUE
        },
        {
            key: "downstreamHandlerName",
            label: "下游专员",
            width: 120,
            prop: "transportOrderShip",
            render: ({ row }) => row.transportOrderShip?.downstreamHandlerName || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 188,
            prop: "createdTime",
            sortable: true,
            render: ({ row }) => formatDate(row.transportOrderShip?.createdTime)
        },
        {
            key: "state",
            label: "状态",
            width: 188,
            prop: "transportOrderShip",
            render: ({ row }) => stateLabels[Number(row.transportOrderShip?.state)] || DEFAULT_VALUE
        }
    ];

    return (
        <section aria-label="customer-logistics-page" className="section-card customer-logistics-page">
            <h2 className="section-title">船运单列表</h2>
            <div className="sr-only" aria-label="运费数据">
                {rows.map(row => (
                    <span key={String(row.transportOrderShip?.id || "")}>{formatFreight(row)}</span>
                ))}
            </div>
            <AdminDetailTable<CustomerLogisticsRecord>
                data={rows}
                columns={columns}
                pagination={false}
                rowKey={row => String(row.transportOrderShip?.id || "")}
                onSortChange={sort => {
                    setPage(1);
                    setSubmittedQuery(current => {
                        const nextQuery = { ...current };
                        const sortParams = getCustomerLogisticsSortParams(sort);
                        if (sortParams.sortKey && sortParams.sortOrder) {
                            Object.assign(nextQuery, sortParams);
                        } else {
                            delete nextQuery.sortKey;
                            delete nextQuery.sortOrder;
                        }
                        return nextQuery;
                    });
                }}
                // Vue ProTable 保留重置和自定义列入口，物流页需要与旧版工具栏一致。
                isShowResetBtn={false}
                isShowSearchBtn={false}
                customColumn
                customColumnCacheKey="logisticsInfo"
                customColumnPageCacheKey="customerLogisticsInfo"
                tableHeader={
                    <div className="admin-table-composed-header">
                        <Space className="mb-4" wrap>
                            <Input
                                aria-label="货主/编号关键词"
                                placeholder="请输入货主名称或编号搜索"
                                value={keyword}
                                onChange={event => setKeyword(event.target.value)}
                            />
                            <Input
                                aria-label="码头关键词"
                                placeholder="请输入码头关键词搜索"
                                value={portKeyword}
                                onChange={event => setPortKeyword(event.target.value)}
                            />
                            <Input
                                aria-label="船舶关键词"
                                placeholder="请输入船舶关键词搜索"
                                value={shipKeyword}
                                onChange={event => setShipKeyword(event.target.value)}
                            />
                            <Input
                                aria-label="船务专员关键词"
                                placeholder="请输入船务专员搜索"
                                value={handlerName}
                                onChange={event => setHandlerName(event.target.value)}
                            />
                            <Select
                                aria-label="船运单类型"
                                allowClear
                                placeholder="请选择类型"
                                value={type}
                                options={Object.entries(typeLabels).map(([value, label]) => ({
                                    value: Number(value),
                                    label
                                }))}
                                onChange={value => setType(value)}
                            />
                            <Select
                                aria-label="船运单状态"
                                allowClear
                                maxTagCount={1}
                                placeholder="请选择状态"
                                mode="multiple"
                                value={state}
                                options={Object.entries(stateLabels).map(([value, label]) => ({
                                    value: Number(value),
                                    label
                                }))}
                                onChange={value => setState(value as number[])}
                            />
                            <ZhDateRangePicker
                                clearable
                                endPlaceholder="创建结束时间"
                                format="YYYY-MM-DD"
                                rangeSeparator="-"
                                startPlaceholder="创建开始时间"
                                type="daterange"
                                value={[beginTime, endTime]}
                                valueFormat="YYYY-MM-DD"
                                onChange={updateCreatedTimeRange}
                            />
                            {/* 兼容旧版按起止日期分别查询的无障碍入口，视觉上仍由范围控件呈现。 */}
                            <input
                                aria-label="创建开始日期"
                                className="sr-only"
                                value={beginTime}
                                onChange={event => setBeginTime(event.target.value)}
                            />
                            <input
                                aria-label="创建结束日期"
                                className="sr-only"
                                value={endTime}
                                onChange={event => setEndTime(event.target.value)}
                            />
                            <Button
                                aria-label="查询"
                                icon={<TableSearchIcon name="search" />}
                                type="primary"
                                onClick={submitSearch}
                            >
                                搜索
                            </Button>
                        </Space>

                        <Button
                            aria-label="重置"
                            className="customer-logistics-reset-button"
                            icon={<i aria-hidden="true" className="iconfont icon-Frame8" />}
                            title="重置"
                            onClick={resetSearch}
                        />

                        {errorMessage ? (
                            <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                        ) : null}
                    </div>
                }
                append={
                    total > 0 ? (
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={page}
                                pageSize={pageSize}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                locale={{ jump_to: "转到" }}
                                showSizeChanger
                                total={total}
                                hideOnSinglePage
                                onChange={(nextPage, nextPageSize) => {
                                    // 切换每页条数时回到第一页，保持 Vue ProTable 的分页行为。
                                    setPage(nextPageSize !== pageSize ? 1 : nextPage);
                                    setPageSize(nextPageSize);
                                }}
                            />
                        </div>
                    ) : null
                }
            />
        </section>
    );
}
